<template>
  <v-dialog v-model="showDialog" max-width="700px" persistent>
    <v-card flat>
      <v-icon
        class="close"
        @click.prevent="
          showDialog = false
          $emit('close')
        "
        >$sl-close</v-icon
      >
      <h2>{{ title }}</h2>
      <!--  used to display instructor list -->
      <v-list v-if="instructorList && instructorList.length > 0" class="instructor mt-4" flat>
        <v-list-item-group v-model="instructors" :multiple="true" :mandatory="false">
          <template v-for="(item, index) in instructorList">
            <v-list-item
              @click.native="check($event, item.id)"
              :disabled="item.isDisabled"
              v-if="item && item.id"
              :key="item.id"
              :value="item.id"
            >
              <v-list-item-icon>
                <v-icon>$sl-instructor</v-icon>
              </v-list-item-icon>
              <v-list-item-content class="ml-4">
                <v-list-item-title v-if="item.name" v-html="item.name"></v-list-item-title>
                <v-list-item-subtitle>{{ $tc('labels.apprentice-count', item.numApprentices) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon>
                <v-checkbox v-model="instructors" :value="item.id" @click.native="check($event, item.id)"></v-checkbox>
              </v-list-item-icon>
            </v-list-item>
            <v-divider :key="`divider-${index}`" v-if="instructorList && ((index + 1) < instructorList.length)"></v-divider>
          </template>
        </v-list-item-group>
        <!-- Used to list instructor  -->
        <ActionButton class="mt-4" @click="linkInstructor" actionIcon="$sl-link" :isDisabled="isImitatingUser()">{{ $t('labels.link') }}</ActionButton>
      </v-list>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/services/api.js'
import ActionButton from '@/components/Common/ActionButton'
import {InstructorAbilities} from '@/components/mixins'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import {mapActions, mapGetters} from 'vuex'
import {CompanyRoles} from '@/components/mixins'

// @group Company
// LinkInstructorModal component
export default {
  name: 'LinkInstructorModal',

  mixins: [InstructorAbilities, CompanyRoles],

  props: {
    // Should dialog be showed
    show: false,
    // modal title
    title: {
      // `String`
      type: String,
      // The default value is: `function returning a string`
      default: function () {
        return this.$t('labels.link-instructor')
      }
    },
    // save modal button
    savebtn: {
      // `String`
      type: String,
      // The default value is: `function returning a string`
      default: function () {
        return this.$t('labels.link')
      }
    },
    // cancel modal button
    cancelBtn: {
      // `String`
      type: String,
      // The default value is: `function returning a string`
      default: function () {
        return this.$t('labels.cancel')
      }
    },
    //icon
    icon: {
      // `String`
      type: String,
      // cancel modal button
      default: '$sl-link'
    },
    // Company id
    companyId: {
      // `Number`
      type: Number,
      // The default value is: `null`
      default: null
    },
    //Qualification time
    isQualificationTime: {
      type: Boolean,
      default: false
    },
    machineIds: {
      type: Array,
      // The default value is: `[]`
      default: []
    },
    // Instructors array
    linkedInstructors: {
      // `Array`
      type: Array,
      // The default value is: `[]`
      default: []
    },
    // apprentice profession
    apprenticeProfession: {
      // `Number`
      type: Number,
      // The default value is: `null`
      default: null
    }
  },

  data: () => ({
    showDialog: false,
    instructors: [],
    companyInstructors: []
  }),

  components: {
    ActionButton
  },

  computed: {
    ...mapGetters('basic', ['getProfessionById', 'hasProfessionOrientations']),
    ...mapGetters('currentuser', [
      'currentUser',
      'isCompanyUser',
      'isApprentice',
      'isAdminUser',
      'isAdministrator',
      'isInstructor',
      'isDepartmentAdministrator',
      'isCompanyEducationResponsible',
      'isAdmin',
      'isImitatingUser',
    ]),

    /**
     * @vuese
     * Used to list instructors that have a certificate for a specific profession and no more than 3 apprenticenships
     */
    instructorList() {
      let instArray = []

      instArray = filter(this.companyInstructors, instructor => {
        const haveCertificates = this.haveCertificates(
          instructor,
          this.apprenticeProfession,
          this.machineIds,
          !this.isQualificationTime
        )
        return haveCertificates
      })

      if (instArray && instArray.length > 0) {
        instArray = instArray.map(o => {
          if (o.employed) {
            let isDisabled = false

            if (this.hasDepartments(this.$route.params.id)) {
              isDisabled = !this.isSameDepartment(
                o.departmentEmployments,
                this.getCurrentUserDepartmentsIds(this.$route.params.id, this.isAdministrator, this.isDepartmentAdministrator(this.$route.params.id), this.isInstructor(this.$route.params.id)),
                this.hasDepartments(this.$route.params.id)
              ) && !this.isAdministrator
            }

            const machineQualificationIds = o.employed.machinequalifications.map(ma => ma.machineId)

            if (isDisabled == false) {
              if (!this.instructors.find(f => f == o.id)) {
                isDisabled = !this.canHaveAnotherApprentice(
                  o,
                  this.apprenticeProfession,
                  machineQualificationIds,
                  !this.isQualificationTime
                )
              }
            }

            return {
              id: o.id,
              name: `${o.employed.firstName} ${o.employed.lastName}`,
              machineQualificationIds: machineQualificationIds,
              professionIds: o.employed.tradeCertificates.map(tc => tc.professionId),
              employed: o.employed,
              isDisabled: isDisabled,
              apprentices: o.apprentices,
              profession: o.employed.tradeCertificates.map(tc => tc.profession.name).join(', '),
              numApprentices: o.apprentices ? o.apprentices.length : 0
            }
          }
        })
      }
      return instArray
    }
  },

  created() {
    this.init()
  },

  watch: {
    /**
     * @vuese
     * Used to watch show prop and open modal
     */
    show(val) {
      this.showDialog = val
      this.init()
    },
    /**
     * @vuese
     * Used to watch companyId prop and call init function
     */
    companyId() {
      this.init()
    }
  },

  methods: {
    /**
     * @vuese
     * Used to fetch instructors
     * @arg The argument is an integer value representing company id and an string representing type of the list
     */
    init() {
      let id = null
      this.companyId != null ? (id = this.companyId) : (id = this.$route.params.id)
      if (this.linkedInstructors && id) {
        this.instructors = this.linkedInstructors.filter(Boolean).map(o => o.id)
        let type = 'instructor'

        if (this.isQualificationTime == true) {
          type = ''
        }

        API.listEmployments(Number(id), {type: ''})
          .then(res => {
            this.companyInstructors = res.data
          })
          .catch(err => {
            console.log(err)
          })
      }
    },
    /**
     * @vuese
     * Used to andle prevents propagation of the event
     */
    check: function (e, instructorId) {
      e.cancelBubble = true
      let foundIndex = this.companyInstructors.findIndex(c => c.id == instructorId)
      if (foundIndex >= 0) {
        let find = this.instructors.find(i => i == instructorId)
        if (find) {
          this.companyInstructors[foundIndex].apprentices.push({id: find})
        } else {
          this.companyInstructors[foundIndex].apprentices.splice(
            this.companyInstructors[foundIndex].apprentices.indexOf(find),
            1
          )
        }
      }
    },
    /**
     * @vuese
     * Used to link instructor
     */
    linkInstructor() {
      let i = []
      this.instructors.forEach(w => {
        this.companyInstructors.forEach(element => {
          if (element.id == w) {
            i.push(element)
          }
        })
      })
      // Fire linked event
      // @arg The argument is object representing instructor
      this.$emit('linked', i)
      //Used to close the dialog
      this.showDialog = false
    }
  }
}
</script>

